const STORAGE_KEY = 'fatsale:selected-wallet'

const EMPTY_SNAPSHOT = {
    account: '',
    chainId: null,
    provider: null,
    walletId: '',
    walletName: '',
    wallets: [],
    status: 'idle',
    error: '',
}

const KNOWN_WALLETS = [
    {
        id: 'trust',
        name: 'Trust Wallet',
        icon: '/img/wallets/trust-wallet.svg',
        rdns: ['com.trustwallet.app', 'com.trustwallet'],
        names: ['trust wallet', 'trustwallet'],
        matches: (provider) => provider?.isTrust || provider?.isTrustWallet,
    },
    {
        id: 'binance',
        name: 'Binance Wallet',
        icon: '/img/wallets/binance-wallet.png',
        rdns: ['com.binance.wallet', 'com.binance'],
        names: ['binance wallet', 'binance web3 wallet'],
        matches: (provider) => provider?.isBinance || provider?.isBinanceChain,
    },
    {
        id: 'okx',
        name: 'OKX Wallet',
        icon: '/img/wallets/okx-wallet.png',
        rdns: ['com.okex.wallet', 'com.okx.wallet', 'com.okx'],
        names: ['okx wallet', 'okex wallet'],
        matches: (provider) => provider?.isOkxWallet || provider?.isOKXWallet || provider?.isOKExWallet,
    },
    {
        id: 'coinbase',
        name: 'Coinbase Wallet',
        rdns: ['com.coinbase.wallet'],
        names: ['coinbase wallet'],
        matches: (provider) => provider?.isCoinbaseWallet,
    },
    {
        id: 'tokenpocket',
        name: 'TokenPocket',
        rdns: ['pro.tokenpocket'],
        names: ['tokenpocket'],
        matches: (provider) => provider?.isTokenPocket,
    },
    {
        id: 'brave',
        name: 'Brave Wallet',
        rdns: ['com.brave.wallet'],
        names: ['brave wallet'],
        matches: (provider) => provider?.isBraveWallet,
    },
    {
        id: 'metamask',
        name: 'MetaMask',
        icon: '/img/wallets/metamask.svg',
        rdns: ['io.metamask'],
        names: ['metamask'],
        matches: (provider) => provider?.isMetaMask
            && !provider?.isBraveWallet
            && !provider?.isBinance
            && !provider?.isBinanceChain
            && !provider?.isOkxWallet
            && !provider?.isOKXWallet
            && !provider?.isOKExWallet
            && !provider?.isTrust
            && !provider?.isTrustWallet,
    },
]

function normalized(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

function matchesWalletInfo(wallet, info = {}) {
    const rdns = normalized(info.rdns)
    const name = normalized(info.name)
    return wallet.rdns?.some((alias) => rdns === alias || rdns.endsWith(`.${alias}`))
        || wallet.names?.some((alias) => name === alias || name.includes(alias))
}

function isPhantomProvider(provider, info = {}) {
    const rdns = normalized(info.rdns)
    const name = normalized(info.name)
    return Boolean(provider?.isPhantom || rdns === 'app.phantom' || rdns.endsWith('.phantom') || name === 'phantom')
}

function parseChainId(value) {
    if (typeof value === 'number') return value
    if (typeof value === 'bigint') return Number(value)
    if (typeof value !== 'string' || value.length === 0) return null
    return Number.parseInt(value, value.startsWith('0x') ? 16 : 10)
}

function safeIcon(icon, fallback = '') {
    if (typeof icon !== 'string') return fallback
    if (/^data:image\/(?:png|jpeg|jpg|webp|svg\+xml);/i.test(icon)) return icon
    if (/^https:\/\//i.test(icon)) return icon
    return fallback
}

function identifyProvider(provider, info = {}) {
    if (isPhantomProvider(provider, info)) return null
    const metadata = KNOWN_WALLETS.find((wallet) => wallet.matches(provider) || matchesWalletInfo(wallet, info))
    const stableId = metadata?.id || info.rdns || info.uuid || 'browser-wallet'
    return {
        id: String(stableId).toLowerCase(),
        name: metadata?.name || info.name || 'Browser Wallet',
        icon: safeIcon(info.icon, metadata?.icon || ''),
        rdns: info.rdns || '',
        provider,
    }
}

function uniqueId(baseId, wallets) {
    const walletIds = new Set(wallets.map((wallet) => wallet.id))
    if (!walletIds.has(baseId)) return baseId
    let suffix = 2
    while (walletIds.has(`${baseId}-${suffix}`)) suffix += 1
    return `${baseId}-${suffix}`
}

export function createWalletProviderManager({
    windowRef = typeof window !== 'undefined' ? window : null,
    settleMs = 120,
} = {}) {
    let snapshot = { ...EMPTY_SNAPSHOT }
    let wallets = []
    let activeProvider = null
    let activeHandlers = null
    let discoveryStarted = false
    const subscribers = new Set()

    const publish = (patch = {}) => {
        snapshot = { ...snapshot, ...patch, wallets: [...wallets] }
        subscribers.forEach((listener) => listener({ ...snapshot }))
        return { ...snapshot }
    }

    const addWallet = (provider, info = {}) => {
        if (!provider) return
        const existingIndex = wallets.findIndex((wallet) => wallet.provider === provider)
        if (existingIndex >= 0) {
            const identified = identifyProvider(provider, info)
            if (!identified) {
                wallets = wallets.filter((_, index) => index !== existingIndex)
                publish()
                return
            }
            wallets = wallets.map((wallet, index) => index === existingIndex ? {
                ...wallet,
                name: identified.name || wallet.name,
                icon: identified.icon || wallet.icon,
                rdns: info.rdns || wallet.rdns,
            } : wallet)
            publish()
            return
        }
        const wallet = identifyProvider(provider, info)
        if (!wallet) return
        wallet.id = uniqueId(wallet.id, wallets)
        wallets = [...wallets, wallet]
        publish()
    }

    const handleAnnouncement = (event) => {
        if (!event?.detail?.provider) return
        addWallet(event.detail.provider, event.detail.info || {})
    }

    const startDiscovery = () => {
        if (!windowRef || discoveryStarted) return
        discoveryStarted = true
        windowRef.addEventListener?.('eip6963:announceProvider', handleAnnouncement)

        const ethereum = windowRef.ethereum
        const legacyProviders = Array.isArray(ethereum?.providers) ? ethereum.providers : (ethereum ? [ethereum] : [])
        legacyProviders.forEach((provider) => addWallet(provider))
        windowRef.dispatchEvent?.(new Event('eip6963:requestProvider'))
    }

    const discoverWalletProviders = async () => {
        startDiscovery()
        publish({ status: snapshot.account ? 'connected' : 'discovering', error: '' })
        if (settleMs > 0) await new Promise((resolve) => setTimeout(resolve, settleMs))
        return publish({ status: snapshot.account ? 'connected' : 'idle' })
    }

    const removeActiveListeners = () => {
        if (!activeProvider || !activeHandlers || !activeProvider.removeListener) return
        Object.entries(activeHandlers).forEach(([event, handler]) => activeProvider.removeListener(event, handler))
        activeHandlers = null
    }

    const attachActiveListeners = (provider, wallet) => {
        removeActiveListeners()
        activeProvider = provider
        if (!provider?.on) return

        activeHandlers = {
            accountsChanged: (accounts = []) => {
                const account = accounts[0] || ''
                publish({
                    account,
                    status: account ? 'connected' : 'idle',
                    error: '',
                })
            },
            chainChanged: (chainId) => publish({ chainId: parseChainId(chainId), error: '' }),
            disconnect: () => publish({ account: '', provider: null, status: 'idle' }),
        }
        Object.entries(activeHandlers).forEach(([event, handler]) => provider.on(event, handler))
        publish({ walletId: wallet.id, walletName: wallet.name })
    }

    const connectWallet = async (walletId) => {
        if (!discoveryStarted) await discoverWalletProviders()
        const wallet = wallets.find((item) => item.id === walletId)
        if (!wallet) throw new Error('Wallet provider is no longer available')

        publish({ status: 'connecting', error: '', walletId: wallet.id, walletName: wallet.name })
        try {
            const [accounts, chainId] = await Promise.all([
                wallet.provider.request({ method: 'eth_requestAccounts' }),
                wallet.provider.request({ method: 'eth_chainId' }),
            ])
            const account = accounts?.[0] || ''
            if (!account) throw new Error('Wallet did not return an account')
            windowRef?.localStorage?.setItem(STORAGE_KEY, wallet.id)
            attachActiveListeners(wallet.provider, wallet)
            return publish({
                account,
                chainId: parseChainId(chainId),
                provider: wallet.provider,
                status: 'connected',
                error: '',
            })
        } catch (error) {
            return Promise.reject(Object.assign(error, {
                walletSnapshot: publish({ status: 'error', error: error?.message || String(error) }),
            }))
        }
    }

    const restoreWallet = async () => {
        await discoverWalletProviders()
        const selectedId = windowRef?.localStorage?.getItem(STORAGE_KEY)
        const wallet = wallets.find((item) => item.id === selectedId) || wallets[0]
        if (!wallet) return publish({ status: 'idle' })

        try {
            const [accounts, chainId] = await Promise.all([
                wallet.provider.request({ method: 'eth_accounts' }),
                wallet.provider.request({ method: 'eth_chainId' }),
            ])
            if (!accounts?.[0]) return publish({ walletId: wallet.id, walletName: wallet.name, status: 'idle' })
            attachActiveListeners(wallet.provider, wallet)
            return publish({
                account: accounts[0],
                chainId: parseChainId(chainId),
                provider: wallet.provider,
                status: 'connected',
                error: '',
            })
        } catch (error) {
            return publish({ status: 'idle', error: error?.message || String(error) })
        }
    }

    const disconnectWallet = () => {
        removeActiveListeners()
        activeProvider = null
        windowRef?.localStorage?.removeItem(STORAGE_KEY)
        return publish({
            ...EMPTY_SNAPSHOT,
            wallets: [...wallets],
        })
    }

    const subscribeWallet = (listener) => {
        subscribers.add(listener)
        listener({ ...snapshot, wallets: [...wallets] })
        return () => subscribers.delete(listener)
    }

    const destroy = () => {
        removeActiveListeners()
        windowRef?.removeEventListener?.('eip6963:announceProvider', handleAnnouncement)
        subscribers.clear()
    }

    return {
        connectWallet,
        destroy,
        disconnectWallet,
        discoverWalletProviders,
        getWalletSnapshot: () => ({ ...snapshot, wallets: [...wallets] }),
        restoreWallet,
        subscribeWallet,
    }
}

let browserManager

function getBrowserManager() {
    if (!browserManager) browserManager = createWalletProviderManager()
    return browserManager
}

export const connectWallet = (walletId) => getBrowserManager().connectWallet(walletId)
export const disconnectWallet = () => getBrowserManager().disconnectWallet()
export const discoverWalletProviders = () => getBrowserManager().discoverWalletProviders()
export const getWalletSnapshot = () => getBrowserManager().getWalletSnapshot()
export const restoreWallet = () => getBrowserManager().restoreWallet()
export const subscribeWallet = (listener) => getBrowserManager().subscribeWallet(listener)
